'use client';

// 배럴(Assets/Logo)이 아니라 파일을 직접 import 한다 — 테스트가 배럴을 일부 심볼만 가진 객체로
// mock 하는 경우 새 아이콘을 배럴로 읽으면 렌더 시 throw 한다 (InviteClientPage 와 같은 이유).
import SvgRenewalArrowUpRight from 'Assets/Icon/svgs/RenewalArrowUpRight.svg';
import SvgRenewalClose from 'Assets/Icon/svgs/RenewalClose.svg';
import SvgViewupAppIcon from 'Assets/Logo/Viewup App Icon.svg';
import { Button, Column, Modal, Typography } from '@viewup/ui';
import { APP_STORE_URL, PLAY_STORE_URL } from 'app/invite/invite.const';
import Image from 'next/image';
import { QRCodeSVG } from 'qrcode.react';

export interface RenewalNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClickStart: () => void;
  /** '오늘 하루 안보기' 버튼. onClose 와 달리 다음 로컬 자정까지 다시 열지 않게 하는 쪽이다. */
  onHideToday: () => void;
  /** 'pc' 는 시작하기 버튼 대신 스토어 QR 2개를 보여준다. 기본값은 'mobile'. */
  variant?: 'mobile' | 'pc';
}

// 피그마 신규 디자인(Mobile Web 리뉴얼 안내 팝업)의 색. 대응하는 theme 토큰이 없다 —
// InviteClientPage 의 DESIGN_COLOR 와 같은 값이다 (viewupTheme 의 text.primary 는 #191919).
const DESIGN_COLOR = {
  brand: '#0077FF',
  textPrimary: '#001E40',
  textSecondary: '#576A7C',
};

// sds Button(size XL)은 고정 height·좌우 padding·body.regular 라벨이라 디자인(높이 48 = 세로 padding 12 +
// 라벨 Bold 16/24, 폭 100%)과 맞지 않아 보충한다. 라벨은 Button 내부 Typography(span)를 겨냥한다.
// hover/active 는 variant 가 :hover/:active 로 색을 덮어쓰므로 같은 셀렉터로 다시 눌러준다.
const START_BUTTON_CSS = {
  '& span': { fontSize: 16, fontWeight: 700, lineHeight: '24px' },
  ':active': { backgroundColor: DESIGN_COLOR.brand },
  ':hover': { backgroundColor: DESIGN_COLOR.brand },
  backgroundColor: DESIGN_COLOR.brand,
  borderRadius: 8,
  boxSizing: 'border-box',
  color: '#FFFFFF',
  height: 48,
  minHeight: 0,
  paddingBlock: 0,
  width: '100%',
} as const;

const CLOSE_BUTTON_STYLE = {
  background: 'none',
  border: 0,
  cursor: 'pointer',
  display: 'block',
  height: 32,
  padding: 0,
  position: 'absolute',
  right: 20,
  top: 20,
  width: 32,
} as const;

const HIDE_TODAY_BUTTON_STYLE = {
  background: 'none',
  border: 0,
  color: DESIGN_COLOR.textSecondary,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: '20px',
  // 터치 영역을 높이 36 으로 키운다(라벨 20 + 위아래 8). 시각 간격은 marginTop 과 합쳐 기존 16 그대로.
  marginTop: 8,
  padding: '8px 12px',
  textAlign: 'center',
  textDecoration: 'underline',
} as const;

const STORE_QR_SIZE = 96;

const STORE_LINK_STYLE = {
  alignItems: 'flex-start',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  textDecoration: 'none',
  width: 104,
} as const;

const STORE_LABEL_STYLE = {
  color: DESIGN_COLOR.textSecondary,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: '24px',
} as const;

const STORE_LABEL_ARROW_ROW_STYLE = {
  alignItems: 'center',
  display: 'flex',
  gap: 4,
} as const;

const STORE_LINKS = [
  { href: PLAY_STORE_URL, label: '구글 앱스토어', ariaLabel: '구글 앱스토어 바로가기' },
  { href: APP_STORE_URL, label: 'IOS 앱스토어', ariaLabel: 'IOS 앱스토어 바로가기' },
] as const;

export const RenewalNoticeModal = ({
  isOpen,
  onClose,
  onClickStart,
  onHideToday,
  variant = 'mobile',
}: RenewalNoticeModalProps) => {
  return (
    // sds Modal 은 radix dialog 를 포털로 띄우고 dim(bg.dimmer.60 = rgba(0,0,0,0.6))·중앙 정렬·
    // 흰 카드를 이미 제공한다. 카드 폭만 좁은 폰에서도 좌우 20px 여백이 남도록 보충한다.
    <Modal
      exitHandler={onClose}
      hasCloseButton={false}
      height='auto'
      isOpen={isOpen}
      isResponsive={false}
      styledComponents={{
        root: {
          css: { borderRadius: 8, maxWidth: 360, whiteSpace: 'normal' },
          p: 0,
        },
      }}
      width='calc(100% - 40px)'
    >
      <button aria-label='닫기' onClick={onClose} style={CLOSE_BUTTON_STYLE} type='button'>
        <Image alt='' height={32} src={SvgRenewalClose} width={32} />
      </button>

      <Column alignItems='center' css={{ boxSizing: 'border-box', padding: '64px 20px 32px' }}>
        <Image alt='뷰업' height={80} src={SvgViewupAppIcon} width={80} />

        <Column alignItems='center' css={{ marginBottom: 20, marginTop: 20 }} gap={8}>
          <Typography
            css={{
              color: DESIGN_COLOR.textPrimary,
              fontSize: 28,
              fontWeight: 700,
              lineHeight: '36px',
              textAlign: 'center',
            }}
            fontType='header2.bold'
          >
            뷰업이 <span style={{ color: DESIGN_COLOR.brand }}>리뉴얼</span> 됐어요!
          </Typography>
          <Typography
            css={{ color: DESIGN_COLOR.textSecondary, lineHeight: '24px', textAlign: 'center' }}
            fontType='body.regular'
          >
            <b style={{ fontWeight: 700 }}>더 새로워진 뷰업</b>을 앱으로 만나보세요.
          </Typography>
        </Column>

        {variant === 'pc' ? (
          <div style={{ display: 'flex', gap: 40, justifyContent: 'center' }}>
            {STORE_LINKS.map(({ href, label, ariaLabel }) => (
              <a
                aria-label={ariaLabel}
                href={href}
                key={href}
                rel='noopener noreferrer'
                style={STORE_LINK_STYLE}
                target='_blank'
              >
                <QRCodeSVG
                  bgColor='#FFFFFF'
                  fgColor='#000000'
                  marginSize={0}
                  size={STORE_QR_SIZE}
                  value={href}
                />
                <div style={STORE_LABEL_STYLE}>
                  <div>{label}</div>
                  <div style={STORE_LABEL_ARROW_ROW_STYLE}>
                    바로가기
                    <Image alt='' height={16} src={SvgRenewalArrowUpRight} width={16} />
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <Button css={START_BUTTON_CSS} onClick={onClickStart} size='XL' variant='solid.primary'>
            새로운 뷰업 시작하기
          </Button>
        )}

        <button onClick={onHideToday} style={HIDE_TODAY_BUTTON_STYLE} type='button'>
          오늘 하루 안보기
        </button>
      </Column>
    </Modal>
  );
};
